import React, { useCallback, useEffect, useState } from 'react';
import { collection, doc, getDocs, writeBatch } from 'firebase/firestore';
import { FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { unitsOf } from '../../lib/permissions';
import { getAreaGrant } from '../../lib/session';
import { BUTTON, CARD, Notice, SIZE, SkeletonRows } from '../ui/kit';

// Messages and Archives are read unit by unit: each entry carries `units`, the lower-case units
// of its channel or service (same computation as the Firestore rules). This tool adds it to the
// entries written before, once, after the deployment.
export const SOURCES = [
  // A message belongs to its channel (`unit`), or for older ones to the sender's service.
  { collection: 'messages', label: 'Messages', unitOf: (entry) => ('unit' in entry ? entry.unit : entry.userService) },
  { collection: 'archives', label: 'Archives', area: 'archives', unitOf: (entry) => entry.service },
];

export const expectedUnits = (source, entry) => unitsOf(typeof source.unitOf(entry) === 'string' ? source.unitOf(entry) : '');
const same = (a, b) => Array.isArray(a) && a.length === b.length && a.every((value, index) => value === b[index]);
const BATCH = 400;

const UnitsUpgrade = () => {
  const [scan, setScan] = useState({ status: 'loading', rows: [] }); // rows: [{ source, pending: [{ id, units }], locked }]
  const [run, setRun] = useState({ state: 'idle', done: 0, total: 0 });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setScan((current) => ({ ...current, status: 'loading' }));
    try {
      const rows = await Promise.all(
        SOURCES.map(async (source) => {
          if (source.area && !getAreaGrant(source.area)) return { source, pending: [], locked: true };
          const snapshot = await getDocs(collection(db, source.collection));
          const pending = snapshot.docs
            .map((item) => ({ id: item.id, entry: item.data() }))
            .map(({ id, entry }) => ({ id, units: expectedUnits(source, entry), current: entry.units }))
            .filter((item) => !same(item.current, item.units))
            .map(({ id, units }) => ({ id, units }));
          return { source, pending, locked: false, count: snapshot.size };
        })
      );
      setScan({ status: 'ready', rows });
    } catch (loadError) {
      setScan({ status: 'error', rows: [] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = scan.rows.reduce((count, row) => count + row.pending.length, 0);

  const start = async () => {
    setError('');
    setRun({ state: 'running', done: 0, total });
    let done = 0;
    try {
      for (const row of scan.rows) {
        for (let index = 0; index < row.pending.length; index += BATCH) {
          const batch = writeBatch(db);
          row.pending.slice(index, index + BATCH).forEach((item) => batch.update(doc(db, row.source.collection, item.id), { units: item.units }));
          // eslint-disable-next-line no-await-in-loop
          await batch.commit();
          done += Math.min(BATCH, row.pending.length - index);
          setRun({ state: 'running', done, total });
        }
      }
      setRun({ state: 'done', done, total });
    } catch (runError) {
      setError('La mise à niveau s’est interrompue. Relancez-la : les entrées déjà traitées ne le seront pas deux fois.');
      setRun({ state: 'idle', done, total });
    }
    load();
  };

  const running = run.state === 'running';
  return (
    <section className={`${CARD} p-6 sm:p-7`}>
      {scan.status === 'loading' && !running ? (
        <div className="w-64">
          <SkeletonRows rows={2} />
        </div>
      ) : scan.status === 'error' ? (
        <Notice tone="red">Les entrées n’ont pas pu être lues. Vérifiez la connexion, puis rechargez la page.</Notice>
      ) : (
        <div className="flex flex-col gap-[16px]">
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {scan.rows.map((row) => (
              <li key={row.source.collection} className="flex flex-wrap items-baseline justify-between gap-2 text-[14.5px]">
                <span className="font-medium text-ink">{row.source.label}</span>
                <span className="text-black/55">
                  {row.locked
                    ? 'ouvrez d’abord l’espace avec votre code dans cet onglet, puis revenez ici'
                    : row.pending.length
                      ? `${row.pending.length} entrée${row.pending.length > 1 ? 's' : ''} à mettre à niveau sur ${row.count}`
                      : `à jour (${row.count} entrée${row.count > 1 ? 's' : ''})`}
                </span>
              </li>
            ))}
          </ul>
          {run.state === 'done' && (
            <Notice tone="green" icon={FiCheckCircle}>
              Mise à niveau terminée : {run.done} entrée{run.done > 1 ? 's' : ''} traitée{run.done > 1 ? 's' : ''}.
            </Notice>
          )}
          {error && <Notice tone="red">{error}</Notice>}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={start} disabled={running || !total} className={`${BUTTON.primary} ${SIZE.md}`}>
              {running ? `Mise à niveau… ${run.done}/${run.total}` : 'Mettre à niveau'}
            </button>
            <button type="button" onClick={load} disabled={running} className={`${BUTTON.secondary} ${SIZE.md}`}>
              <FiRefreshCw size={15} aria-hidden="true" />
              Actualiser
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default UnitsUpgrade;
